import { FunctionComponent } from 'react';
import { DisablePipelineDialogBody } from '../components/DisablePipelineDialogBody';
import { Integration, IntegrationAction } from '../../../shared/interfaces/integration';
import { ConfirmationBodyExtraParams } from '../../../modal/ConfirmModal/ConfirmModal';
import { PipelineStrings } from '../../strings';
import { PipelineAPI } from '../../api/PipelineAPI';
import { ResyncPipelineDialogBody } from '../components/ResyncPipelineDialogBody';
import { getPostConfirmationDeleteDialogProps } from '../../../shared/components/PostConfirmationDeleteDialogProps/PostConfirmationDeleteDialogProps';
import { EnablePipelineDialogBody } from '../components/EnablePipelineDialogBody';
import {
  EntityAction,
  EntityActionConfirmDialogProps,
  EntityActionHandlerType,
  EntityActionType
} from '../../../entity-actions/interfaces/entity-actions';
import { SourceAction } from '../../../shared/interfaces/source';
import { DestinationAction } from '../../../shared/interfaces/destination';

export const PIPELINE_OVERVIEW_DISPLAY_CONFIG_EDIT_ACTION: EntityAction<IntegrationAction.EDIT> = {
  action: IntegrationAction.EDIT,
  type: EntityActionType.INLINE,
  icon: 'edit',
  displayName: 'Edit',
  actionHandlerType: EntityActionHandlerType.NAVIGATE,
  routeOptions: {
    route: '../edit',
    options: {
      relative: 'path'
    }
  }
};

export const PIPELINE_OVERVIEW_DISPLAY_CONFIG_SOURCE_EDIT_ACTION: EntityAction<SourceAction.EDIT> =
  {
    action: SourceAction.EDIT,
    type: EntityActionType.INLINE,
    icon: 'edit',
    displayName: 'Edit',
    actionHandlerType: EntityActionHandlerType.NAVIGATE,
    routeOptions: {
      route: '../edit-source',
      options: {
        relative: 'path'
      }
    }
  };

export const PIPELINE_OVERVIEW_DISPLAY_CONFIG_DESTINATION_EDIT_ACTION: EntityAction<DestinationAction.EDIT> =
  {
    action: DestinationAction.EDIT,
    type: EntityActionType.INLINE,
    icon: 'edit',
    displayName: 'Edit',
    actionHandlerType: EntityActionHandlerType.NAVIGATE,
    routeOptions: {
      route: '../edit-destination',
      options: {
        relative: 'path'
      }
    }
  };

export const PIPELINE_ACTIONS_DISPLAY_DETAILS: EntityAction<IntegrationAction>[] = [
  {
    action: IntegrationAction.EDIT,
    type: EntityActionType.INLINE,
    icon: 'edit',
    displayName: 'Edit',
    actionHandlerType: EntityActionHandlerType.NAVIGATE,
    routeOptions: {
      route: 'edit',
      options: {
        relative: 'route'
      }
    }
  },
  {
    action: IntegrationAction.ENABLE,
    type: EntityActionType.MENU,
    icon: 'play',
    displayName: 'Enable',
    description: 'Starts the Pipeline to ingest and load data to your Destination',
    actionHandlerType: EntityActionHandlerType.CONFIRM
  },
  {
    action: IntegrationAction.DISABLE,
    type: EntityActionType.MENU,
    icon: 'disable',
    displayName: 'Disable',
    description: 'Stops the Pipeline from ingesting or loading data to your Destination',
    actionHandlerType: EntityActionHandlerType.CONFIRM
  },
  {
    action: IntegrationAction.RUN_NOW,
    type: EntityActionType.MENU,
    icon: 'syncing',
    displayName: 'Sync Now',
    description: 'Syncs the Pipeline from latest offset',
    actionHandlerType: EntityActionHandlerType.CONFIRM
  },
  {
    action: IntegrationAction.RESTART,
    type: EntityActionType.MENU,
    icon: 'restart',
    displayName: 'Resync Pipeline',
    description: 'Starts historical load for Pipeline.',
    actionHandlerType: EntityActionHandlerType.CONFIRM
  },
  {
    action: IntegrationAction.DELETE,
    type: EntityActionType.MENU,
    icon: 'delete',
    displayName: 'Delete Pipeline',
    description: 'Delete the Pipeline and stop ingesting data and loading it to the Destination',
    className: 'text-error',
    actionHandlerType: EntityActionHandlerType.CONFIRM
  }
];

export const PIPELINE_ACTIONS_CONFIRMATION_DIALOG_PROPS: EntityActionConfirmDialogProps<
  IntegrationAction,
  Integration
> = {
  [IntegrationAction.RUN_NOW]: {
    title: 'Sync Now?',
    positiveButtonText: 'Sync Now',
    negativeButtonText: 'Cancel',
    positiveBtnProps: {
      icon: 'syncing'
    },
    successMessage: PipelineStrings.runNowSuccessMessage,
    body: 'Syncs the Pipeline from latest offset value',
    actionHandler: args => PipelineAPI.runNow(args.entity.id, { signal: args.signal })
  },
  [IntegrationAction.DISABLE]: {
    title: 'Disable Pipeline?',
    positiveButtonText: 'Disable',
    negativeButtonText: 'Cancel',
    successMessage: PipelineStrings.disablePipelineSuccessMessage,
    BodyComponent: DisablePipelineDialogBody as FunctionComponent<
      Partial<ConfirmationBodyExtraParams<object>>
    >,
    actionHandler: args => PipelineAPI.disablePipeline(args.entity.id, !!args.cancelRunningJobs)
  },

  [IntegrationAction.ENABLE]: {
    title: 'Enable Pipeline?',
    positiveButtonText: 'Enable',
    negativeButtonText: 'Cancel',
    successMessage: PipelineStrings.enablePipelineSuccessMessage,
    BodyComponent: EnablePipelineDialogBody as FunctionComponent<
      Partial<ConfirmationBodyExtraParams<object>>
    >,
    actionHandler: args => PipelineAPI.enablePipeline(args.entity.id)
  },
  [IntegrationAction.RESTART]: {
    title: 'Resync Pipeline?',
    BodyComponent: ResyncPipelineDialogBody as FunctionComponent<
      Partial<ConfirmationBodyExtraParams<object>>
    >,
    positiveButtonText: 'Resync Pipeline',
    positiveBtnProps: {
      icon: 'restart'
    },
    negativeButtonText: 'Cancel',
    successMessage: PipelineStrings.resyncPipelineSuccessMessage,
    actionHandler: args => PipelineAPI.resyncPipeline(args.entity.id, { signal: args.signal })
  },
  [IntegrationAction.DELETE]: {
    title: 'Delete Pipeline?',
    titleContainerClassName: 'text-error',
    positiveButtonText: 'Delete Pipeline',
    negativeButtonText: 'Cancel',
    positiveButtonClass: 'btn-error',
    positiveButtonVariation: 'faded',
    positiveButtonPalette: 'error',
    positiveBtnProps: {
      icon: 'delete'
    },
    body: 'The Pipeline will be permanently deleted, stopping all data ingestion and loading into your Destination.',
    actionHandler: args => PipelineAPI.deletePipeline(args.entity.id, { signal: args.signal }),
    postConfirmationDialogProps: getPostConfirmationDeleteDialogProps('Pipeline', 'tab=edge')
  },
  [IntegrationAction.EDIT]: {},
  [IntegrationAction.CHANGE_SCHEDULE]: {}
};
